import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  BOTTOM_NAVIGATION_CLEARANCE,
  IconAccount,
  IconBottomNavigationRewards,
  IconDialog,
  IconInfoFill,
  ScrollView,
  tokens,
} from '@fhs/ui';
import { useFeatureNavigation } from '@fhs-legacy/frontend/src/hooks/use-feature-navigation';
import { useAuthContext } from '@fhs-legacy/frontend/src/state/auth';
import {
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from '@fhs-legacy/frontend/src/state/crm-events';
import { useLocale } from '@fhs-legacy/frontend/src/state/intl';
import { REGIONS } from '@fhs-legacy/frontend/src/state/intl/types';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';

import { useHrefs } from '../../use-hrefs';

import { AuthenticatedHeader } from './authenticated-header';
import { FhsLogoBackground } from './fhs-logo-background';
import { LoggedOutHeader } from './logged-out-header';
import {
  type NavigationEntry,
  aboutLinks,
  accountLinks,
  flattenNavigationEntries,
  loyaltyLinks,
  supportLinks,
} from './more-navigation';
import { MoreSection } from './more-section';
import { MoreLinkCard } from './shared';

export function MoreScreen() {
  const { isAuthenticated, user } = useAuthContext();
  const { logRBIEvent } = useCRMEventsContext();
  const { region } = useLocale();
  const { cateringHref, foundationHref, careersHref, franchiseHref } = useHrefs();
  const { featureAboutMenuEntriesMobile } = useFeatureNavigation();
  const enableDiagnosticTools = useFlag('enable-diagnostic-tools');
  const cmsLinks = useMemo(
    () =>
      flattenNavigationEntries(
        (featureAboutMenuEntriesMobile?.pages ?? []) as readonly NavigationEntry[]
      ),
    [featureAboutMenuEntriesMobile]
  );

  return (
    <View style={styles.screen}>
      <View pointerEvents="none" style={styles.watermark}>
        <FhsLogoBackground aria-hidden />
      </View>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={[styles.content, !isAuthenticated && styles.loggedOutContent]}>
          {isAuthenticated ? <AuthenticatedHeader /> : <LoggedOutHeader />}
          {isAuthenticated ? (
            <MoreSection
              title="Account & Ordering"
              icon={<IconAccount size={18} color={tokens.colors.$houseDark} aria-hidden />}
              links={accountLinks(cateringHref, region !== REGIONS.CA)}
            />
          ) : null}
          <MoreSection
            title="Loyalty"
            icon={
              <IconBottomNavigationRewards size={18} color={tokens.colors.$houseDark} aria-hidden />
            }
            links={loyaltyLinks}
          />
          <MoreSection
            title="Support"
            icon={<IconDialog size={18} color={tokens.colors.$houseDark} aria-hidden />}
            links={supportLinks(cmsLinks).map(link =>
              link.path === routes.support
                ? {
                    ...link,
                    onPress: () =>
                      logRBIEvent({
                        name: CustomEventNames.PYPESTREAM_SUPPORT_OPEN,
                        type: EventTypes.UserContent,
                        attributes: { userCognitoId: user?.cognitoId },
                      }),
                  }
                : link
            )}
          />
          <MoreSection
            title="About"
            icon={<IconInfoFill size={18} color={tokens.colors.$houseDark} aria-hidden />}
            links={aboutLinks(cmsLinks, { foundationHref, franchiseHref, careersHref })}
          />
          <MoreLinkCard
            links={[{ label: 'Other Terms & Policies', path: '/account/other-terms' }]}
          />
          {enableDiagnosticTools ? (
            <MoreLinkCard links={[{ label: 'Diagnostics', path: routes.diagnostics }]} />
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: tokens.colors.$white },
  scrollContent: {
    paddingBottom: BOTTOM_NAVIGATION_CLEARANCE,
  },
  content: {
    alignSelf: 'center',
    gap: 32,
    maxWidth: 600,
    paddingHorizontal: 16,
    paddingTop: 24,
    width: '100%',
  },
  watermark: {
    bottom: 0,
    height: 319,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    width: 264,
  },
  loggedOutContent: { paddingTop: 0 },
});
